import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiChevronDown, FiCheck } from '../../../icons';
import './CustomSelect.scss';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  options: SelectOption[];
  value?: string;
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  isInvalid?: boolean;
  className?: string;
  loading?: boolean;
  loadingText?: string;
  name?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  options,
  value = '',
  placeholder = 'Select an option',
  onChange,
  disabled = false,
  isInvalid = false,
  className = '',
  loading = false,
  loadingText = 'Loading...'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const selectRef = useRef<HTMLDivElement>(null);
  const optionsRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(option => option.value === value);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setFocusedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (disabled || loading) return;

    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(selectedOption ? options.indexOf(selectedOption) : 0);
        } else if (focusedIndex >= 0) {
          const selectedOpt = options[focusedIndex];
          if (selectedOpt && !selectedOpt.disabled) {
            onChange(selectedOpt.value);
            setIsOpen(false);
            setFocusedIndex(-1);
          }
        }
        break;
      case 'ArrowDown':
        event.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setFocusedIndex(selectedOption ? options.indexOf(selectedOption) : 0);
        } else {
          setFocusedIndex(prev => {
            let nextIndex = prev + 1;
            while (nextIndex < options.length && options[nextIndex].disabled) {
              nextIndex++;
            }
            return nextIndex < options.length ? nextIndex : prev;
          });
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (isOpen) {
          setFocusedIndex(prev => {
            let nextIndex = prev - 1;
            while (nextIndex >= 0 && options[nextIndex].disabled) {
              nextIndex--;
            }
            return nextIndex >= 0 ? nextIndex : prev;
          });
        }
        break;
      case 'Escape':
        setIsOpen(false);
        setFocusedIndex(-1);
        break;
    }
  };

  const handleToggle = () => {
    if (disabled || loading) return;
    setIsOpen(!isOpen);
    if (!isOpen && selectedOption) {
      setFocusedIndex(options.indexOf(selectedOption));
    }
  };

  const handleOptionClick = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    setIsOpen(false);
    setFocusedIndex(-1);
  };

  // Scroll focused option into view
  useEffect(() => {
    if (isOpen && focusedIndex >= 0 && optionsRef.current) {
      const focusedElement = optionsRef.current.children[focusedIndex] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest'
        });
      }
    }
  }, [focusedIndex, isOpen]);

  return (
    <div
      ref={selectRef}
      className={`custom-select ${className} ${isInvalid ? 'is-invalid' : ''} ${disabled ? 'disabled' : ''}`}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
    >
      <motion.div
        className={`select-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleToggle}
        whileHover={!disabled && !loading ? { 
          boxShadow: '0 4px 15px rgba(57, 87, 160, 0.15)' 
        } : undefined}
        whileTap={!disabled && !loading ? { scale: 0.98 } : undefined}
      >
        <span className="select-value">
          {loading ? loadingText : (selectedOption ? selectedOption.label : placeholder)}
        </span>
        <motion.div
          className="select-arrow"
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <FiChevronDown />
        </motion.div>
      </motion.div>

      <AnimatePresence>
        {isOpen && !loading && (
          <motion.div
            ref={optionsRef}
            className="select-dropdown"
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
          >
            {options.length === 0 ? (
              <div className="select-option no-options">No options available</div>
            ) : (
              options.map((option, index) => (
                <motion.div
                  key={option.value}
                  className={`select-option ${option.disabled ? 'disabled' : ''} ${
                    focusedIndex === index ? 'focused' : ''
                  } ${value === option.value ? 'selected' : ''}`}
                  onClick={() => handleOptionClick(option)}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.1, delay: index * 0.02 }}
                >
                  <span className="option-text">{option.label}</span>
                  {value === option.value && (
                    <motion.div
                      className="option-check"
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ duration: 0.2 }}
                    >
                      <FiCheck />
                    </motion.div>
                  )}
                </motion.div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CustomSelect;